import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  GetServicesByNodeQuery,
  GetServicesByNodeQueryVariables,
  Service,
} from "../../../API";
import { GET_SERVICES_BY_NODE } from "../../../common/operations/queries";
import { errorNotification } from "../../../common/variables/notification";
import client from "../../../configs/apolloClient";

interface ServicesByDeviceMap {
  [deviceId: string]: Service[];
}

export const useGetServicesByDevices = (
  devices: Array<{ id: string; nodeId: string }>
) => {
  const [servicesByDevice, setServicesByDevice] = useState<ServicesByDeviceMap>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [completedDevices, setCompletedDevices] = useState<Set<string>>(
    new Set()
  );
  const pendingQueriesRef = useRef<Set<string>>(new Set());

  const [getServicesByNode] = useLazyQuery<
    GetServicesByNodeQuery,
    GetServicesByNodeQueryVariables
  >(GET_SERVICES_BY_NODE, {
    fetchPolicy: "network-only",
  });

  const handleRequestError = (deviceId: string) => {
    client.cache.reset();

    errorNotification();

    // Remove from pending and mark as completed to avoid infinite retries
    pendingQueriesRef.current.delete(deviceId);

    setCompletedDevices(prev => new Set([...Array.from(prev), deviceId]));
  };

  const fetchServicesByNode = async (
    nodeId: string,
    nextToken?: string
  ): Promise<Service[]> => {
    try {
      const response = await getServicesByNode({
        variables: {
          nodeId: nodeId,
          nextToken,
        },
      });

      if (response.error) {
        handleRequestError(nodeId);

        return [];
      }

      const services = response.data?.getServicesByNode?.items ?? [];

      // If there's a nextToken, fetch more pages
      if (response.data?.getServicesByNode?.nextToken) {
        const additionalServices = await fetchServicesByNode(
          nodeId,
          response.data.getServicesByNode.nextToken
        );

        return [...services, ...additionalServices];
      }

      return services;
    } catch (error) {
      handleRequestError(nodeId);

      return [];
    }
  };

  const fetchServicesForNode = async (nodeId: string) => {
    if (pendingQueriesRef.current.has(nodeId) || completedDevices.has(nodeId)) {
      return;
    }

    pendingQueriesRef.current.add(nodeId);

    try {
      const services = await fetchServicesByNode(nodeId);

      // Map services to devices by matching deviceId
      const servicesByDeviceForNode: ServicesByDeviceMap = {};

      services.forEach(service => {
        if (service.deviceId) {
          // Extract the actual device ID from the service's deviceId
          // Service deviceId format: C#<customerId>DE#<deviceId> or just DE#<deviceId>
          let actualDeviceId = service.deviceId;

          // If it has the C# prefix, extract just the DE# part
          if (
            service.deviceId.includes("C#") &&
            service.deviceId.includes("DE#")
          ) {
            const deIndex = service.deviceId.indexOf("DE#");

            actualDeviceId = service.deviceId.substring(deIndex);
          }
          // If it doesn't have DE# prefix at all, add it
          else if (!service.deviceId.includes("DE#")) {
            actualDeviceId = `DE#${service.deviceId}`;
          }

          if (!servicesByDeviceForNode[actualDeviceId]) {
            servicesByDeviceForNode[actualDeviceId] = [];
          }

          servicesByDeviceForNode[actualDeviceId].push(service);
        }
      });

      setServicesByDevice(prev => ({
        ...prev,
        ...servicesByDeviceForNode,
      }));
    } finally {
      pendingQueriesRef.current.delete(nodeId);

      setCompletedDevices(prev => new Set([...Array.from(prev), nodeId]));
    }
  };

  // Fetch services for all devices
  useEffect(() => {
    if (devices.length === 0) {
      setServicesByDevice({});

      setCompletedDevices(new Set());

      return;
    }

    setLoading(true);

    // Extract unique node IDs from devices
    const uniqueNodeIds = Array.from(
      new Set(devices.map(device => device.nodeId).filter(Boolean))
    );

    // Filter out nodes we've already fetched or are currently fetching
    const nodesToFetch = uniqueNodeIds.filter(
      nodeId =>
        !completedDevices.has(nodeId) && !pendingQueriesRef.current.has(nodeId)
    );

    if (nodesToFetch.length === 0) {
      setLoading(false);

      return;
    }

    // Fetch services for all nodes in parallel
    Promise.all(nodesToFetch.map(fetchServicesForNode)).finally(() => {
      setLoading(false);
    });
  }, [devices.map(d => `${d.id}-${d.nodeId}`).join(",")]); // Use device id and node id for stable dependency

  // Get services for a specific device
  const getServicesForDevice = useMemo(() => {
    return (deviceId: string): Service[] => {
      // Normalize the device ID to match how we store it
      let normalizedDeviceId = deviceId;

      // Ensure it has DE# prefix
      if (!deviceId.startsWith("DE#")) {
        normalizedDeviceId = `DE#${deviceId}`;
      }

      const services = servicesByDevice[normalizedDeviceId] ?? [];

      return services;
    };
  }, [servicesByDevice]);

  // Get all services (flattened)
  const allServices = useMemo(() => {
    return Object.values(servicesByDevice).flat();
  }, [servicesByDevice]);

  return {
    servicesByDevice,
    getServicesForDevice,
    allServices,
    loading,
    completedDevices: Array.from(completedDevices),
  };
};
